import React, { forwardRef } from 'react';
import { Bell, Trash2 } from 'lucide-react';
import type { Notification } from '../../types';

interface HeaderNotificationsProps {
  show: boolean;
  onToggle: () => void;
  notifications: Notification[];
  loading: boolean;
  unreadCount: number;
  onMarkAllRead: () => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  formatDateTime: (date: string) => string;
  zIndex: number;
}

const HeaderNotifications = forwardRef<HTMLDivElement, HeaderNotificationsProps>(({
  show,
  onToggle,
  notifications,
  loading,
  unreadCount,
  onMarkAllRead,
  onDelete,
  formatDateTime,
  zIndex,
}, ref) => (
  <div ref={ref} className="relative">
    <button
      onClick={onToggle}
      className="p-2 rounded-md text-white hover:text-cyan-400 hover:bg-white/10 transition-colors relative touch-manipulation"
      aria-label="View notifications"
    >
      <Bell className="w-5 h-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center min-w-[1.25rem]">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
    {show && (
      <div
        className="absolute right-0 mt-2 w-72 lg:w-80 bg-gray-900/95 backdrop-blur-md rounded-md shadow-lg border border-white/20 z-50"
        style={{ zIndex }}
      >
        <div className="p-3 border-b border-white/20 flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-white">Notifications</h3>
          {notifications.length > 0 && unreadCount > 0 && (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="text-xs text-cyan-400 hover:text-cyan-300 whitespace-nowrap touch-manipulation"
            >
              Mark all as read
            </button>
          )}
        </div>
        <div className="notifications-scroll scrollbar-hide max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-400 text-sm">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">No notifications</div>
          ) : (
            notifications.map((notification) => {
              const isUnread = !notification.read_at;
              return (
                <div
                  key={notification.id}
                  className={`p-3 border-b border-white/10 hover:bg-white/10 flex flex-col gap-1 ${isUnread ? 'border-l-2 border-l-cyan-400 bg-white/5' : 'opacity-80'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm text-white min-w-0 flex-1 ${isUnread ? 'font-medium' : ''}`}>
                      {notification.message}
                    </p>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      {isUnread && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-cyan-500/20 text-cyan-400">
                          New
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => onDelete(e, notification.id)}
                        className="p-1 rounded text-gray-400 hover:text-red-400 hover:bg-white/10 transition-colors touch-manipulation"
                        aria-label="Delete notification"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-300">{formatDateTime(notification.created_at)}</p>
                </div>
              );
            })
          )}
        </div>
      </div>
    )}
  </div>
));

HeaderNotifications.displayName = 'HeaderNotifications';

export default HeaderNotifications;
